<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import {
    KorrekturKalibrierungsfaktor,
    KorrekturKalibrierungsfaktorCollection,
} from '@/models/KorrekturKalibrierungsfaktor';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'jahr' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new KorrekturKalibrierungsfaktorCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(async () => {
    if (collection.loading.value !== true) {
        await collection.query();
    }
});

function editRecord(r: KorrekturKalibrierungsfaktor) {
    router.push({ name: 'bg_jw_korrektur_kalibrierungsfaktor_edit', params: { id: r?.id } });
}

async function deleteRecord(r: KorrekturKalibrierungsfaktor) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_jw_korrektur_kalibrierungsfaktor_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('korrektur_kalibrierungsfaktor')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as KorrekturKalibrierungsfaktor)"
            @deleterecord="deleteRecord($event as KorrekturKalibrierungsfaktor)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="kanton_id"
                sortable
                :header="$t('kanton')"
                sort-field="kanton_kuerzel"
                :showFilterMenu="false"
            >
                <template #body="slotProps">{{ slotProps.data.kanton_kuerzel }}</template>
            </Column>
            <Column field="jahr" :header="$t('jahr')" sortable filter-field="jahr" :showFilterMenu="false">
                <template #body="slotProps">
                    <div>{{ slotProps.data.jahr }}</div>
                </template>
            </Column>
            <Column field="faktor" :header="$t('kalibrierungsfaktor')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
